import {Alert, Card, closeCurrentDialog, DataStatus, openDialog} from 'platform/components';
import {Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {useState} from 'react';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  AftersalesPaymentMethod,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentsQuery,
} from '@dms/api/sales';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseOrderJobsQuery,
  useGetServiceCaseServiceOrderMissingMandatoryFieldsApiQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes, workshopRoutes} from '@dms/routes';
import {
  MandatoryFieldMapValue,
  MandatoryFieldsErrorModal,
  queryParams,
  useTenant,
} from '@dms/shared';

import {
  composePath,
  isNotNilOrEmpty,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
} from 'shared/web';

import {CheckoutPaymentForm} from './components/CheckoutPaymentForm';
import {CorrectiveTaxDocumentsList} from './components/CorrectiveTaxDocumentsList';
import {DepositPaymentsCard} from './components/DepositPaymentsCard';
import {ForeginCurrencyPaymentCard} from './components/ForeginCurrencyPaymentCard';
import {
  getMessage,
  getAction,
  shouldShowAlert,
  ContextTarget,
} from './constants/issueRestrictionAlerts';
import {IssueRestrictionResponse} from './types/IssueRestrictionResponse';

interface AftersalesCheckoutPaymentProps extends RequiredTestIdProps {
  checkoutId: string;
  defaultPaymentType?: AftersalesPaymentMethod;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  serviceCaseId?: string;
  serviceOrderId?: string;
}

type PathType = '/vehicles/:id/edit' | '/vehicles/:id/edit/:tab';

const VEHICLE_MANDATORY_FIELDS_MAP: Record<string, MandatoryFieldMapValue> = {
  vehicleRegistrationPlate: {
    title: i18n.t('entity.vehicle.labels.licensePlate'),
    href: vehiclesRoutes.editV2,
    formField: 'registrationPlate',
  },
  vehicleVin: {
    title: i18n.t('entity.vehicle.labels.vin'),
    href: vehiclesRoutes.editV2,
    formField: 'vin',
  },
  vehicleFirstRegistrationOn: {
    title: i18n.t('entity.vehicle.labels.firstRegistration'),
    href: vehiclesRoutes.editV2,
    formField: 'firstRegistrationOn',
  },
  vehicleWarrantyDate: {
    title: i18n.t('entity.vehicle.labels.warrantyUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'warrantyDate',
  },
  vehicleTechnicalInspectionValidUntil: {
    title: i18n.t('entity.vehicle.labels.inspectionValidUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'technicalInspectionValidUntil',
  },
};

const SERVICE_ORDER_MANDATORY_FIELDS_MAP: Record<string, MandatoryFieldMapValue> = {
  completionAtTime: {
    title: i18n.t('entity.order.labels.mandatoryField-completionAtTime'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'completionAtTime',
  },
  completionAtDate: {
    title: i18n.t('entity.order.labels.mandatoryField-completionAtDate'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'completionAtDate',
  },
  totalPriceEstimated: {
    title: i18n.t('entity.order.labels.mandatoryField-totalPriceEstimated'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'totalPriceEstimated',
  },
  provider: {
    title: i18n.t('entity.order.labels.mandatoryField-provider'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'provider',
  },
  approvalNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-approvalNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'approvalNumber',
  },
  serviceCenterId: {
    title: i18n.t('entity.order.labels.mandatoryField-serviceCenterId'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'serviceCenterId',
  },
  serviceOrderGroupId: {
    title: i18n.t('entity.order.labels.mandatoryField-serviceOrderGroupId'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'serviceOrderGroupId',
  },
  mileage: {
    title: i18n.t('entity.order.labels.mandatoryField-mileage'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'mileage',
  },
  fuelTank: {
    title: i18n.t('entity.order.labels.mandatoryField-fuelTank'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'fuelTank',
  },
  generatingOrderSheet: {
    title: i18n.t('entity.order.labels.mandatoryField-generatingOrderSheet'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'generatingOrderSheet',
  },
  insuranceCompany: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceCompany'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceCompany',
  },
  insuranceType: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceType'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceType',
  },
  insuranceEventNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceEventNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceEventNumber',
  },
  insuranceClaimNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceClaimNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceClaimNumber',
  },
  insuranceClaimOccurrenceAt: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceClaimOccurrenceAt'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceClaimOccurrenceAt',
  },
  applicationOfTheLaw: {
    title: i18n.t('entity.order.labels.applicationOfTheLaw'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'applicationOfTheLaw',
  },
  claimSerialNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-claimSerialNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'claimSerialNumber',
  },
  costCenter: {
    title: i18n.t('entity.order.labels.mandatoryField-costCenter'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'costCenter',
  },
};

export function AftersalesCheckoutPayment(props: AftersalesCheckoutPaymentProps) {
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();
  const [issueRestrictions, setIssueRestrictions] = useState<IssueRestrictionResponse[]>([]);

  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});
  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useGetCheckoutPaymentsQuery({checkoutId: props.checkoutId});

  const {data: missingMandatoryFieldsData} =
    useGetServiceCaseServiceOrderMissingMandatoryFieldsApiQuery(
      {
        serviceCaseId: props.serviceCaseId ?? '',
        serviceOrderId: props.serviceOrderId ?? '',
        event: 'BEFORE_CLOSING',
      },
      {
        skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId),
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
      }
    );

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId ?? ''},
    {skip: isNilOrEmpty(props.serviceCaseId)}
  );

  const {data: serviceOrderJobs} = useGetServiceCaseOrderJobsQuery(
    {
      serviceCaseId: props.serviceCaseId ?? '',
      serviceOrderId: props.serviceOrderId ?? '',
    },
    {
      skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId),
    }
  );

  const isLoading = isCheckoutLoading || isPaymentsLoading;
  const isError = isCheckoutError || isPaymentsError;

  const contextTarget: ContextTarget =
    checkoutData?.context?.target === 'service-order'
      ? 'service-order'
      : checkoutData?.context?.target === 'direct-sale'
        ? 'direct-sale'
        : undefined;

  const hasMissingMandatoryFields =
    isNotNil(missingMandatoryFieldsData) && (missingMandatoryFieldsData?.length ?? 0) > 0;

  const areAllPaymentsConcept = !!paymentsData?.every(
    ({paymentState}) => paymentState === 'CONCEPT'
  );

  const balancePayment = paymentsData?.find((payment) => payment.discriminator === 'BALANCE');

  const hasDepositPayment = paymentsData?.some((payment) => payment.discriminator === 'DEPOSIT');

  const hasNotPaidDepositPayment = paymentsData?.some(
    (payment) => payment.discriminator === 'DEPOSIT' && payment.paymentState !== 'PAID'
  );

  const correctivePayments = paymentsData?.filter(
    (payment) =>
      payment.discriminator === 'BALANCE_CORRECTION_FULL' ||
      payment.discriminator === 'BALANCE_CORRECTION_PARTIAL'
  );
  const rentalOrderId = serviceOrderJobs?.find(
    (job) => job?.isCarRental && isNotNilOrEmpty(job?.carRentalDetails?.rentalOrderId)
  )?.carRentalDetails?.rentalOrderId;

  const handleRedirect = (field: MandatoryFieldMapValue) => {
    // Close the dialog first
    closeCurrentDialog();

    // Check if this is a service order field
    const isServiceOrderField = field.href === workshopRoutes.serviceCaseDetail;

    if (isServiceOrderField) {
      // Navigate to service order with orderTab query param using composePath with queryParams
      const path = composePath(workshopRoutes.serviceCaseDetail, {
        params: {id: props.serviceCaseId ?? ''},
        queryParams: {
          [queryParams.SERVICE_CASE_ORDER_ID]: props.serviceOrderId ?? '',
          [queryParams.SERVICE_CASE_ORDER_TAB]: field.tab ?? 'detail',
        },
      });

      // Use setTimeout to ensure dialog closes before navigation
      setTimeout(() => {
        navigate(path);
      }, 100);
      return;
    }

    // Vehicle field - navigate to vehicle edit page
    const path = composePath(field.href as PathType, {
      params: {id: serviceCase?.vehicleId, tab: isNotNilOrEmpty(field.tab) ? field.tab : undefined},
    });

    navigate(path);
  };

  const showMandatoryFieldsErrorModal = () => {
    // Split fields by resource type
    const vehicleFields =
      missingMandatoryFieldsData?.filter((field) => field?.resourceId === 'VEHICLE') ?? [];
    const serviceOrderFields =
      missingMandatoryFieldsData?.filter((field) => field?.resourceId === 'SERVICE_ORDER') ?? [];

    // Map vehicle fields
    const transformedVehicleFields: MandatoryFieldMapValue[] = vehicleFields
      .map((field) => VEHICLE_MANDATORY_FIELDS_MAP[field?.mandatoryField ?? ''])
      .filter(Boolean);

    // Map service order fields
    const transformedServiceOrderFields: MandatoryFieldMapValue[] = serviceOrderFields
      .map((field) => SERVICE_ORDER_MANDATORY_FIELDS_MAP[field?.mandatoryField ?? ''])
      .filter(Boolean);

    // Combine both types
    const transformedMissingFields = [
      ...transformedVehicleFields,
      ...transformedServiceOrderFields,
    ];

    const translatedFields = transformedMissingFields.map((field) => field.title);

    openDialog(
      <MandatoryFieldsErrorModal
        mandatoryFields={transformedMissingFields}
        translatedFields={translatedFields}
        onSubmit={handleRedirect}
      />,
      {
        id: 'mandatory-fields-error-modal',
        title: i18n.t('entity.mandatoryFields.errorModal.title'),
      }
    );
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4}>
        <Show when={hasMissingMandatoryFields}>
          <Alert
            variant="error"
            title={`${i18n.t('entity.mandatoryFields.labels.missingFieldsCount')} (${missingMandatoryFieldsData?.length ?? 0})`}
            hyperlinks={[
              {
                title: i18n.t('general.actions.view'),
                onClick: () => {
                  showMandatoryFieldsErrorModal();
                },
              },
            ]}
          />
        </Show>
        {checkoutData && (
          <Card variant="inlineGrey">
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.order.labels.totalAmount')}
              </Text>
              <Heading size={3}>
                {formatCurrency(
                  checkoutData?.totalPrice?.withVat?.amount ?? 0,
                  checkoutData?.totalPrice?.withVat?.currency ?? tenant.tenantCurrency,
                  2
                )}
              </Heading>
              <Text size="xSmall" color="secondary">
                {`${formatCurrency(
                  checkoutData?.totalPrice?.withoutVat?.amount ?? 0,
                  checkoutData?.totalPrice?.withoutVat?.currency ?? tenant.tenantCurrency,
                  2
                )} ${i18n.t('entity.order.labels.withoutVat')}`}
              </Text>
            </VStack>
          </Card>
        )}
        <Show when={checkoutData?.isEditForeignCurrencyAllowed}>
          <ForeginCurrencyPaymentCard
            checkoutId={props.checkoutId}
            areAllPaymentsConcept={areAllPaymentsConcept}
            isEditingDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('foreginCurrency', props)}
          />
        </Show>
        <Show when={checkoutData?.isEditDepositAllowed}>
          <DepositPaymentsCard
            checkoutId={props.checkoutId}
            defaultPaymentType={props.defaultPaymentType}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
            data-testid={suffixTestId('depositPayments', props)}
          />
        </Show>
        <HStack spacing={2}>
          <Icon value="action/payment" />
          <Heading size={4}>
            {hasDepositPayment
              ? i18n.t('entity.checkout.labels.supplementaryPayment')
              : i18n.t('entity.checkout.labels.totalPayment')}
          </Heading>
        </HStack>
        {balancePayment && (
          <>
            <Card variant="inlineGrey">
              <VStack spacing={4}>
                <VStack spacing={2}>
                  <Show when={hasNotPaidDepositPayment}>
                    <Alert
                      title={i18n.t(
                        'general.notifications.paymentNotPossibleBeforeDepositsPaid.title'
                      )}
                      variant="warning"
                    />
                  </Show>
                  <Show when={issueRestrictions.length > 0}>
                    <VStack spacing={2}>
                      {issueRestrictions.map((restriction) => {
                        if (!shouldShowAlert(restriction.code, contextTarget)) {
                          return null;
                        }

                        const message = getMessage(restriction.code);
                        const action = getAction(
                          restriction.code,
                          props.serviceCaseId || checkoutData?.context?.id,
                          contextTarget,
                          {rentalOrderId: rentalOrderId ?? undefined}
                        );

                        return (
                          <Alert
                            key={restriction.code}
                            title={message}
                            variant="error"
                            hyperlinks={
                              action && action.href
                                ? [
                                    {
                                      title: action.title || i18n.t('general.actions.view'),
                                      onClick: () => {
                                        if (action.href) {
                                          navigate(action.href);
                                        }
                                      },
                                    },
                                  ]
                                : undefined
                            }
                            type="banner"
                          />
                        );
                      })}
                    </VStack>
                  </Show>
                </VStack>
                <CheckoutPaymentForm
                  paymentId={balancePayment.checkoutPaymentId}
                  checkoutId={props.checkoutId}
                  isSubmitButtonDisabled={
                    isNilOrEmpty(checkoutData?.billingInformation?.customerInformation.id) ||
                    isNilOrEmpty(checkoutData?.billingInformation?.contractInformation?.id)
                  }
                  customerId={checkoutData?.billingInformation?.customerInformation.id ?? ''}
                  defaultPaymentType={props.defaultPaymentType}
                  isForeignCurrencyAllowed={checkoutData?.isForeignCurrencyPayment}
                  isEditingDisabled={props.isEditingDisabled}
                  onRestrictionsChange={setIssueRestrictions}
                  data-testid={suffixTestId('payment', props)}
                />
              </VStack>
            </Card>
            <CorrectiveTaxDocumentsList
              checkoutId={props.checkoutId}
              correctivePayments={correctivePayments}
              data-testid={suffixTestId('correctiveTaxDocuments', props)}
            />
          </>
        )}
      </VStack>
    </DataStatus>
  );
}
